import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, Phone, MessageCircle } from 'lucide-react';
import { supplierSchema, type SupplierFormData, type Supplier } from '../../types/supplier';
import { type PurchaseOrder } from '../../types/purchaseOrder';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { formatCurrency } from '../../utils/format';
import { useSettingsStore } from '../../store/settingsStore';
import { generateCode } from '../../utils/codeGenerator';
import { useLanguage } from '../../features/language/useLanguage';

interface SupplierFormProps {
  initialData?: Supplier;
  onSubmit?: (data: SupplierFormData) => Promise<void>;
  onCancel: () => void;
  readOnly?: boolean;
  existingCodes?: string[];
}

export const SupplierForm: React.FC<SupplierFormProps> = ({ 
  initialData, 
  onSubmit, 
  onCancel, 
  readOnly = false,
  existingCodes = []
}) => {
  const { t } = useLanguage();
  const { currencySymbol } = useSettingsStore();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<SupplierFormData>({
    resolver: zodResolver(supplierSchema),
    defaultValues: initialData || { name: '', code: '', contactName: '', email: '', phone: '', status: 'Active' }
  });

  const name = watch('name');
  const code = watch('code');

  // Auto-generate code when name changes
  useEffect(() => {
    if (readOnly || initialData?.code) return;
    
    if (name && !code) {
      const generated = generateCode(name, existingCodes);
      if (generated) {
        setValue('code', generated, { shouldValidate: true });
      }
    }
  }, [name, code, readOnly, initialData, existingCodes, setValue]);

  useEffect(() => {
    if (readOnly && initialData?.id) {
      loadHistory(initialData.id);
    }
  }, [readOnly, initialData]);

  const loadHistory = async (supplierId: string) => {
    try {
      setLoadingHistory(true);
      const allPOs = await purchaseOrderService.getAll();
      const history = allPOs
        .filter(po => po.supplier === supplierId)
        .sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
      setPurchaseOrders(history);
    } catch (error) {
      console.error('Failed to load purchase order history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const email = watch('email');
  const phone = watch('phone');

  const handleFormSubmit = async (data: SupplierFormData) => {
    if (onSubmit) {
      await onSubmit(data);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.supplier.name')}</label>
            <input 
              data-test-id="supplier-form-name" 
              {...register('name')} 
              disabled={readOnly}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500" 
            />
            {errors.name && <p className="text-red-600 text-sm">{errors.name.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.supplier.code3')}</label>
            <input 
              data-test-id="supplier-form-code" 
              {...register('code')} 
              maxLength={3}
              placeholder={t('forms.common.autoGenerated')}
              disabled={true}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 bg-gray-100 text-gray-500 cursor-not-allowed uppercase" 
            />
            <p className="text-xs text-gray-500 mt-1">{t('forms.common.generatedAutomatically')}</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('forms.supplier.contactName')}</label>
          <input 
            data-test-id="supplier-form-contact-name" 
            {...register('contactName')} 
            disabled={readOnly}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500" 
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.common.email')}</label>
            <div className="flex gap-2 mt-1">
              <input 
                data-test-id="supplier-form-email" 
                {...register('email')} 
                disabled={readOnly}
                className="block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500" 
              />
              {email && (
                <a 
                  href={`mailto:${email}`}
                  className="flex items-center justify-center p-2 text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100"
                  title={t('forms.actions.sendEmail')}
                >
                  <Mail className="h-5 w-5" />
                </a>
              )}
            </div>
            {errors.email && <p className="text-red-600 text-sm">{errors.email.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.common.phone')}</label>
            <div className="flex gap-2 mt-1">
              <input 
                data-test-id="supplier-form-phone" 
                {...register('phone')} 
                disabled={readOnly}
                className="block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500" 
              />
              {phone && (
                <>
                  <a 
                    href={`https://wa.me/${phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center p-2 text-green-600 bg-green-50 rounded-md hover:bg-green-100"
                    title={t('forms.actions.openWhatsapp')}
                  >
                    <MessageCircle className="h-5 w-5" />
                  </a>
                  <a 
                    href={`tel:${phone}`}
                    className="flex items-center justify-center p-2 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                    title={t('forms.actions.call')}
                  >
                    <Phone className="h-5 w-5" />
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('forms.common.status')}</label>
          <select 
            data-test-id="supplier-form-status" 
            {...register('status')} 
            disabled={readOnly}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500"
          >
            <option value="Active">{t('forms.common.statusActive')}</option>
            <option value="Inactive">{t('forms.common.statusInactive')}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('forms.common.internalNotes')}</label>
          <textarea 
            data-test-id="supplier-form-internal-notes" 
            {...register('internalNotes')} 
            disabled={readOnly}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500" 
            rows={3} 
          />
        </div>
      </div>

      {readOnly && (
        <div className="mt-8 border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('forms.supplier.purchaseOrderHistory')}</h3>
          {loadingHistory ? (
            <LoadingSpinner text={t('forms.common.loadingHistory')} />
          ) : purchaseOrders.length === 0 ? (
            <p className="text-gray-500 text-sm italic">{t('forms.supplier.emptyPurchaseOrders')}</p>
          ) : (
            <div className="overflow-x-auto border rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('forms.common.poNumber')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('forms.common.date')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('forms.common.status')}</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('forms.common.amount')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {purchaseOrders.map((po) => (
                    <tr key={po.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{po.orderNumber}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{new Date(po.orderDate).toLocaleDateString()}</td>
                      <td className="px-4 py-2 text-sm">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide
                          ${po.status === 'ordered' ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20' :
                            po.status === 'draft' ? 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20' :
                            po.status === 'cancelled' ? 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20' :
                            'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-600/20'}`}>
                          <span className={`h-1.5 w-1.5 rounded-full
                            ${po.status === 'ordered' ? 'bg-green-500' :
                              po.status === 'draft' ? 'bg-blue-500' :
                              po.status === 'cancelled' ? 'bg-red-500' :
                              'bg-gray-400'}`} />
                          {t(`forms.supplier.poStatus.${po.status}`, undefined, po.status)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(po.totalAmount, currencySymbol)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <button type="button" data-test-id="supplier-form-cancel" onClick={onCancel} className="px-4 py-2 border rounded-md">
          {readOnly ? t('forms.actions.close') : t('auth.common.cancel')}
        </button>
        {!readOnly && (
          <button type="submit" data-test-id="supplier-form-submit" disabled={isSubmitting} className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
            {isSubmitting ? t('forms.actions.saving') : t('forms.actions.save')}
          </button>
        )}
      </div>
    </form>
  );
};
